import type {
  NotificationDto,
  NotificationPageDto,
  UnreadCountDto,
} from '@reelplicate/api-client/model';
import {
  getGetNotificationUnreadCountQueryKey,
  getListNotificationsQueryKey,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '@reelplicate/api-client/notifications';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';

type Cached = NotificationPageDto | InfiniteData<NotificationPageDto> | undefined;

/**
 * Mark read for the bell and the notifications page. Both lists (the
 * popover's page and the full page's infinite pages) live under the same key
 * prefix, so marking updates every cached copy and the unread count at once,
 * then puts them back if the server says no.
 */
export function useNotificationActions(workspaceId: string) {
  const queryClient = useQueryClient();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const listKey = getListNotificationsQueryKey(workspaceId);
  const countKey = getGetNotificationUnreadCountQueryKey(workspaceId);

  /** Marks matching unread notifications read in the cache; returns the undo. */
  const markLocally = (isTarget: (item: NotificationDto) => boolean) => {
    const lists = queryClient.getQueriesData<Cached>({ queryKey: listKey });
    const previousCount = queryClient.getQueryData<UnreadCountDto>(countKey);
    const now = new Date().toISOString();
    const marked = new Map<string, NotificationDto>();
    const markPage = (page: NotificationPageDto): NotificationPageDto => ({
      ...page,
      items: page.items.map((item) => {
        if (item.readAt || !isTarget(item)) return item;
        marked.set(item.id, item);
        return { ...item, readAt: now };
      }),
    });
    for (const [key, data] of lists) {
      if (!data) continue;
      const next: Cached =
        'pages' in data ? { ...data, pages: data.pages.map(markPage) } : markPage(data);
      queryClient.setQueryData(key, next);
    }
    if (previousCount) {
      const mentions = [...marked.values()].filter((item) => item.mention).length;
      queryClient.setQueryData<UnreadCountDto>(countKey, {
        all: Math.max(0, previousCount.all - marked.size),
        mentions: Math.max(0, previousCount.mentions - mentions),
      });
    }
    return () => {
      for (const [key, data] of lists) queryClient.setQueryData(key, data);
      queryClient.setQueryData(countKey, previousCount);
    };
  };

  const settle = () => void queryClient.invalidateQueries({ queryKey: countKey });

  return {
    /** One notification opened or acted on. */
    markOne: (notification: Pick<NotificationDto, 'id' | 'readAt'>) => {
      if (notification.readAt) return;
      const undo = markLocally((item) => item.id === notification.id);
      markRead.mutate(
        { id: workspaceId, notificationId: notification.id },
        { onError: undo, onSettled: settle },
      );
    },
    /** Everything up to the newest one the person has seen. */
    markAll: (before: string, filter?: 'mentions') => {
      const undo = markLocally(
        (item) => item.occurredAt <= before && (filter !== 'mentions' || item.mention),
      );
      markAllRead.mutate(
        { id: workspaceId, data: { before, ...(filter ? { filter } : {}) } },
        { onError: undo, onSettled: settle },
      );
    },
  };
}
